
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UpdateFormData } from './types';

export const useSelectOptions = (formData: UpdateFormData) => {
  const { data: savedStates } = useQuery({
    queryKey: ['saved-states'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('saved_states')
        .select('country, league, year')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const uniqueCountries = useMemo(() => {
    if (!savedStates) return [];
    return Array.from(new Set(savedStates.map(state => state.country))).filter(Boolean).sort();
  }, [savedStates]);

  const availableLeagues = useMemo(() => {
    if (!savedStates || !formData.country) return [];
    return Array.from(new Set(
      savedStates
        .filter(state => state.country === formData.country)
        .map(state => state.league)
    )).filter(Boolean).sort();
  }, [savedStates, formData.country]);

  const availableYears = useMemo(() => {
    if (!savedStates || !formData.country || !formData.league) return [];
    return Array.from(new Set(
      savedStates
        .filter(state => 
          state.country === formData.country && 
          state.league === formData.league
        )
        .map(state => state.year)
    )).filter(Boolean).sort((a, b) => b.localeCompare(a));
  }, [savedStates, formData.country, formData.league]);

  const frequencies = [
    { value: 'hourly', label: 'Every Hour' },
    { value: 'daily', label: 'Once a Day' },
    { value: 'weekly', label: 'Once a Week' },
    { value: 'monthly', label: 'Once a Month' }
  ];

  return {
    uniqueCountries,
    availableLeagues,
    availableYears,
    frequencies
  };
};
